import produce from 'immer';
import {Spec} from 'immutability-helper';
import React from 'react';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {DataTable} from 'notebook/data-tables/DataTableConstants';
import DataTableEditable from 'notebook/data-tables/DataTableEditable';
import * as DataTableUtil from 'notebook/data-tables/DataTableUtil';
import {getDataTableName} from 'notebook/data-tables/DataTableUtil';
import {createCollapseId} from 'notebook/mediocre/base-plugin/createCollapseId';
import MediocrePlugins from 'notebook/mediocre/base-plugin/plugins';
import wyciwyp from 'notebook/mediocre/clipboard/wyciwyp';
import {StoreDependencyMap} from 'notebook/mediocre/EditorTypes';
import {TableItemProps} from 'notebook/mediocre/MediocreConstants';
import {MediocreSearchResultProps} from 'notebook/mediocre/search/SearchResultProps';
import {MediocreSearchResult, TextMatcher} from 'notebook/mediocre/search/SearchResults';
import {
  buildTableSearchResult,
  doesTableCellMatch,
  getSearchResultsForColumnNames,
  getSearchResultsForTableName,
} from 'notebook/mediocre/search/TableSearchUtil';
import {makeItemFromWyciwypPart} from 'notebook/notes/CreateOps';
import NoteConstants from 'notebook/notes/NoteConstants';
import {NoteEditor, NoteEditorPlugin, NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import {RegistrationTable} from 'notebook/registration-tables/RegistrationTableConstants';
import * as RegistrationTableUtil from 'notebook/registration-tables/RegistrationTableUtil';
import {CollapsedTableState} from 'notebook/tables/collapse/reduceCollapsedTablesUI';
import {isTableCollapsed} from 'notebook/tables/collapse/useTableCollapse';
import {EvaluatedCell, OutputTableMap} from 'notebook/tables/excess/Evaluator';
import makeTableHTML from 'notebook/tables/export/makeTableHTML';
import {Table, TableData} from 'notebook/tables/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {NotePart} from 'notebook/util/NoteUtilConstants';
import {getStore} from 'redux-stores/Store';
import {MODEL_NAMES} from 'results/ResultsNamingConstants';
import StorableStore from 'samples/StorableStore';
import {WorkflowSelector} from 'workflows/reduceWorkflows';

function _getExportableDataTable(
  editor: NoteEditor,
  item: NotePart & {item: DataTable}
): {fullTable: TableData; computedTable: Array<Array<EvaluatedCell>>} {
  const workflowSelector = new WorkflowSelector(getStore().getState().models.workflows);
  const stageRuns = workflowSelector.getStageRunsForEntry(editor.props.fileId);
  const stageRunSampleById = workflowSelector.getStageRunSampleById();
  const batchesById = StorableStore.getBatches();
  const fileSummariesById = getStore().getState().models.fileSummariesById;

  return DataTableUtil.convertDataTableForExport(
    item.item,
    editor.props.computedTables,
    editor.props.makeFullTableMap(editor.props),
    batchesById,
    fileSummariesById,
    stageRuns,
    stageRunSampleById
  );
}

type DataTableItemProps = TableItemProps & {registrationTables: Array<RegistrationTable>};

const DATA_TABLE_PLUGIN: NoteEditorPlugin = {
  type: ModelTypeConstants.DATA_TABLE,
  typeClass: 'is-table',
  title: `Structured ${MODEL_NAMES.Result_Table}`,
  icon: 'note-table',
  component: {
    selectionInit: MediocrePlugins.table.selectionInit,
    selectionAtStart: MediocrePlugins.table.selectionAtStart,
    selectionAtEnd: MediocrePlugins.table.selectionAtEnd,
    required: [],
    // Plugin methods are always called using Function.prototype.call,
    // so `this` refers to the editor component.
    getItemProps(this: NoteEditor, item: NotePart & {item: DataTable}, index: number): DataTableItemProps {
      return {
        registrationTables: RegistrationTableUtil.getRegistrationTablesInNote(this.props.note),
        ...MediocrePlugins.getTableItemProps.call(this, item, index),
      };
    },
    getIsCollapsed(
      item: NotePart & {item: DataTable},
      editorProps: NoteEditorProps,
      collapsedStateById: CollapsedTableState
    ) {
      const rowCount = item.item.rows.length;
      const cellCount = rowCount > 0 ? rowCount * item.item.rows[0].cells.length : 0;
      return isTableCollapsed({
        collapseId: createCollapseId(editorProps.fileId, item.item.config.name),
        collapsedStateById,
        rowCount,
        cellCount,
        tableKind: null,
      });
    },
    // Plugin methods are always called using Function.prototype.call,
    // so `this` refers to the editor component.
    make(
      this: NoteEditor,
      item: NotePart & {item: DataTable},
      index: number,
      editorProps: NoteEditorProps,
      itemProps: DataTableItemProps,
      searchResultProps: MediocreSearchResultProps
    ): JSX.Element {
      const cid = editorProps.getCid(item);
      const {item: dataTable} = item;
      return (
        <DataTableEditable
          {...itemProps}
          cid={cid}
          dataTable={dataTable}
          editor={this}
          editorProps={editorProps}
          fileId={editorProps.fileId}
          forRun={false}
          indentation={this.getIndentation ? this.getIndentation(item) : undefined}
          index={index}
          locked={editorProps.locked}
          updateHandler={this.tableUpdateHandler}
          viewer={editorProps.viewer}
          {...searchResultProps}
        />
      );
    },
  },
  itemToText(this: NoteEditor, item: NotePart & {item: DataTable}): string {
    const {fullTable, computedTable} = _getExportableDataTable(this, item);
    return TableUtil.tableEditableToText(fullTable, computedTable);
  },
  // Plugin methods are always called using Function.prototype.call,
  // so `this` refers to the editor component.
  itemToHTML(this: NoteEditor, $el: JQuery, item: NotePart & {item: DataTable}): JQuery {
    $el.attr(NoteConstants.COPY_NOTE_LINKED_OBJECT_DATA_PARENT_ID, this.props.fileId);
    const {fullTable, computedTable} = _getExportableDataTable(this, item);
    $el.append(
      makeTableHTML(fullTable, computedTable, {
        minI: 0,
        minJ: 0,
        maxI: fullTable.table.length - 1,
        maxJ: fullTable.table[0].length - 1,
      })
    );
    return $el;
  },
  // Plugin method are always called using Function.prototype.call,
  // so `this` refers to the editor component.
  itemFromHTML(this: NoteEditor, $el: JQuery, item: NotePart & {item: DataTable}): NotePart | null {
    let allowDataTablePaste = false;
    // If we're pasting into the same entry we were copied from, we are eligible to paste as a
    // real data table as long as none of the euids are already taken.
    if ($el.attr(NoteConstants.COPY_NOTE_LINKED_OBJECT_DATA_PARENT_ID) === this.props.fileId) {
      const entryEuids = new Set(DataTableUtil.getEntryEuids(this.props.note));
      const tableEuids = DataTableUtil.getDataTableEuids(item.item);
      allowDataTablePaste = tableEuids.every((euid: string) => !entryEuids.has(euid));
    }
    // If this is the wrong entry or an euid is taken, downgrade to a normal ELN table (which
    // is already present in the HTML on the clipboard).
    if (!allowDataTablePaste) {
      const wyciwypPart = wyciwyp.processNodes(Array.from($el), {allowTables: true})[0];
      if (wyciwypPart) {
        return makeItemFromWyciwypPart(wyciwypPart, this.props.usedTableNames()).item;
      } else {
        return null;
      }
    }
    return produce(item, (draftItem) => {
      draftItem.item = DataTableUtil.cloneDataTable(draftItem.item as DataTable);
    });
  },
  getStoreDependenciesForSpreadsheetEval(item: NotePart & {item: DataTable}): StoreDependencyMap {
    return {};
  },
  // This must stay in sync with table.name returned from getContent
  getSpreadsheetTableName({item}: NotePart & {item: DataTable}): string {
    return getDataTableName(item);
  },
  getContent({item}: NotePart & {item: DataTable}): {table: Table} {
    return {table: DataTableUtil.convertDataTableToTable(item)};
  },
  setContent({table, item}: {table?: TableData; item?: DataTable}): Spec<NotePart> {
    if (item) {
      return {$merge: {item}};
    } else if (table) {
      return {
        item: {
          $apply(dataTable: DataTable): DataTable {
            return DataTableUtil.mergeTableUpdateIntoDataTable(dataTable, table);
          },
        },
      };
    }

    assert(false, 'setContent invoked without content to set.');
  },

  *getSearchResults(
    item: NotePart,
    matcher: TextMatcher,
    searchOverFormulas: boolean,
    computedTables: OutputTableMap | null,
    isCollapsed: boolean
  ): Generator<MediocreSearchResult> {
    const dataTable = item.item as DataTable;
    const name = getDataTableName(dataTable);
    // Search over the table name
    yield* getSearchResultsForTableName(matcher, name, item.cid);

    if (!isCollapsed) {
      // Search over the table column names
      const columnNames = dataTable.config.columns.map((column) => column.name ?? '');
      yield* getSearchResultsForColumnNames(matcher, columnNames, item.cid);

      const name = getDataTableName(dataTable);
      const computedTable = computedTables?.[name];
      assert(computedTable != null, 'Computed table not found in plugin', {
        computedTableNames: computedTables ? Object.keys(computedTables) : [],
      });
      for (const [i, row] of dataTable.rows.entries()) {
        for (const [j, cell] of row.cells.entries()) {
          if (doesTableCellMatch(matcher, cell, computedTable[i][j], searchOverFormulas)) {
            yield buildTableSearchResult(item.cid, i, j);
          }
        }
      }
    }
  },
};

/*
 * This plugin is no longer supported, please do not further proliferate its use.
 */
export default DATA_TABLE_PLUGIN;
